import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import { useAuth } from '../context/AuthContext';

const Navigation = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, logout } = useAuth();

  const navItems = [
    { id: 'home', path: '/', label: t('nav.home') },
    { id: 'about', path: '/about', label: t('nav.about') },
    { id: 'contact', path: '/contact', label: t('nav.contact') },
    { id: 'faq', path: '/faq', label: t('nav.faq') }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const handleLogout = () => {
    logout();
    setIsMenuOpen(false);
  };

  return (
    <nav className={`hemp-navbar fixed w-full z-50 transition-all duration-300 ${
      scrolled ? 'bg-hemp-green-800 shadow-lg' : 'bg-hemp-green-700'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex-shrink-0 flex items-center">
            <img 
              src="/logo.png" 
              alt="Thai Rural Hemp Project Logo"
              className="h-10 w-auto mr-3"
            />
            <Link to="/" className="text-xl font-bold text-white">
              {t('nav.logo')}
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:block">
            <div className="ml-10 flex items-baseline space-x-4">
              {navItems.map((item) => (
                <Link
                  key={item.id}
                  to={item.path}
                  className={`hemp-navbar-link ${
                    isActive(item.path) ? 'hemp-navbar-link-active' : ''
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Auth Buttons */}
          <div className="hidden md:block">
            {user ? (
              <div className="flex items-center space-x-4">
                <Link
                  to="/portal"
                  className="px-3 py-2 rounded-md text-sm font-medium text-white hover:bg-hemp-green-600"
                >
                  {t('nav.portal') || 'Portal'}
                </Link>
                <span className="text-white">{user.username}</span>
                <button
                  onClick={handleLogout}
                  className="px-3 py-2 rounded-md text-sm font-medium text-white hover:bg-hemp-green-600"
                >
                  {t('nav.logout')}
                </button>
              </div>
            ) : (
              <div className="flex space-x-4">
                <Link
                  to="/login"
                  className="px-3 py-2 rounded-md text-sm font-medium text-white hover:bg-hemp-green-600"
                >
                  {t('nav.login')}
                </Link>
                <Link
                  to="/register"
                  className="px-3 py-2 rounded-md text-sm font-medium text-white hover:bg-hemp-green-600"
                >
                  {t('nav.register')}
                </Link>
              </div>
            )}
          </div>

          {/* Language Switcher and Mobile Menu Button */}
          <div className="flex items-center">
            <div className="hidden md:block mr-4">
              <LanguageSwitcher />
            </div>
            
            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="hemp-navbar-link inline-flex items-center justify-center p-2 rounded-md"
                aria-expanded="false"
              >
                <span className="sr-only">Open main menu</span>
                <svg
                  className={`${isMenuOpen ? 'hidden' : 'block'} h-6 w-6`}
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
                <svg
                  className={`${isMenuOpen ? 'block' : 'hidden'} h-6 w-6`}
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      <div className={`${isMenuOpen ? 'block' : 'hidden'} md:hidden`}>
        <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
          {navItems.map((item) => (
            <Link
              key={item.id}
              to={item.path}
              className={`block px-3 py-2 rounded-md text-base font-medium ${
                isActive(item.path)
                  ? 'bg-hemp-green-600 text-white'
                  : 'text-white hover:bg-hemp-green-600'
              }`}
            >
              {item.label}
            </Link>
          ))}
          
          {/* Mobile Auth Buttons */}
          {user ? (
            <div className="pt-4 pb-3 border-t border-hemp-green-600">
              <Link
                to="/portal"
                className="block px-3 py-2 rounded-md text-base font-medium text-white hover:bg-hemp-green-600"
              >
                {t('nav.portal') || 'Portal'}
              </Link>
              <div className="px-3 py-2">
                <span className="text-white">{user.username}</span>
              </div>
              <button
                onClick={handleLogout}
                className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-white hover:bg-hemp-green-600"
              >
                {t('nav.logout')}
              </button>
            </div>
          ) : (
            <div className="pt-4 pb-3 border-t border-hemp-green-600">
              <Link
                to="/login"
                className="block px-3 py-2 rounded-md text-base font-medium text-white hover:bg-hemp-green-600"
              >
                {t('nav.login')}
              </Link>
              <Link
                to="/register"
                className="block px-3 py-2 rounded-md text-base font-medium text-white hover:bg-hemp-green-600"
              >
                {t('nav.register')}
              </Link>
            </div>
          )}
          
          <div className="pt-4 pb-3 border-t border-hemp-green-600">
            <div className="flex items-center px-3">
              <LanguageSwitcher />
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
